/**
 * App-level controller for the What's New experience.
 *
 * - Polls unread count on mount + every 5 minutes
 * - On first mount, auto-opens the modal if there's unread content AND the
 *   user hasn't dismissed it for this browser session
 * - Renders the bell button (with unread badge) so users can re-open the
 *   modal on demand
 *
 * Lives in the TopBar; one instance per session.
 */

import { useQuery } from "@tanstack/react-query";
import { Bell } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { apiFetch } from "@/lib/api";
import { useAuthState } from "@/lib/auth";
import { cn } from "@/lib/utils";

import { useUnreadCount } from "../api";
import { getDismissToken, WhatsNewModal } from "./WhatsNewModal";

interface UserReadStatus {
  user_id: string;
  last_read_at: string | null;
  dismissed_session: string | null;
}

export function WhatsNewLauncher() {
  const { user } = useAuthState();
  const [open, setOpen] = useState(false);
  const autoOpenedRef = useRef(false);

  // Only fire user-facing queries once we're authenticated; otherwise the
  // 401-redirect interceptor would kick the page out.
  const enabled = !!user;

  const unread = useUnreadCount({
    enabled,
    refetchInterval: 5 * 60_000,
  });

  const userRead = useQuery({
    queryKey: ["whats_new", "user-read"],
    queryFn: () => apiFetch<UserReadStatus>("/whats-new/user-read"),
    enabled,
    staleTime: 60_000,
  });

  // Auto-open on first load if there's unread content and the user hasn't
  // dismissed for this session. Runs at most once per mount.
  useEffect(() => {
    if (autoOpenedRef.current) return;
    if (!enabled) return;
    if (!unread.data || unread.data.count === 0) return;
    if (getDismissToken()) return;
    autoOpenedRef.current = true;
    setOpen(true);
  }, [enabled, unread.data]);

  if (!user) return null;

  const count = unread.data?.count ?? 0;
  const hasUnread = count > 0;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={cn(
          "relative inline-flex h-8 w-8 items-center justify-center rounded-md transition-colors",
          hasUnread
            ? "text-brand hover:bg-brand/10"
            : "text-muted-foreground hover:bg-accent hover:text-foreground",
        )}
        aria-label={hasUnread ? `What's New (${count} unread)` : "What's New"}
        title={hasUnread ? `${count} unread update${count === 1 ? "" : "s"}` : "What's New"}
      >
        <Bell className="h-4 w-4" />
        {hasUnread && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-brand px-1 text-[10px] font-bold leading-none text-white">
            {count > 99 ? "99+" : count}
          </span>
        )}
      </button>

      <WhatsNewModal
        open={open}
        onClose={() => setOpen(false)}
        lastReadAt={userRead.data?.last_read_at ?? null}
      />
    </>
  );
}
